import { type BookmarksTeamBacklogAttributes } from './generated/BookmarksTeamBacklogAttributes';
import { model } from '../modelBuilder';
import ApplicationModel, { attr } from '../ApplicationModel';
import Feature from './Feature';
import Requirement from './Requirement';
import { compareNullsLast } from 'components/framework/lib/compareUtils';
import { isNil } from 'lodash';
import BookmarksWorkflowBoard from './BookmarksWorkflowBoard';

function getBookmarkRecordPosition(feature) {
  return feature.bookmarksRecordPositions?.find(
    p => p.bookmarkType === BookmarksTeamBacklog.bookmarkName
  );
}

interface BookmarksTeamBacklog extends BookmarksTeamBacklogAttributes {}

@model
class BookmarksTeamBacklog extends ApplicationModel {
  static OBJECT_CLASSES = [Feature, Requirement];

  // Start auto-generated
  static typename: 'BookmarksTeamBacklog' = 'BookmarksTeamBacklog';
  get typename(): 'BookmarksTeamBacklog' {
    return 'BookmarksTeamBacklog';
  }

  static bookmarkName = 'Bookmarks::TeamBacklog';

  static fields = {
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    records: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  constructor(attributes, options = {}) {
    super(attributes, options);
    this.sortFeatures(); // sometimes order is stale in cache
  }

  // Add a writer for records even though this is a readonly field
  set records(value) {
    this.setAttribute('records', value);
  }

  get features() {
    return this.records.filter(r => r.constructor.typename === 'Feature');
  }

  static buildDefaultRecordScope(buildRecordScope) {
    const scopes = BookmarksTeamBacklog.OBJECT_CLASSES.map(objectClass =>
      buildRecordScope(objectClass)
    );

    return scopes.reduce((acc, scope) => acc.union(scope));
  }

  /**
   * Cannot be visible in this bookmark if its an an iteration or on the
   * workflow board
   *
   * @param {Aha.RecordUnion} record
   */
  canContainRecord(record) {
    if (record.iteration?.id) return false;
    if (!isNil(record.recordPosition(BookmarksWorkflowBoard.bookmarkName)))
      return false;

    return true;
  }

  containsRecord(record) {
    return (
      getBookmarkRecordPosition(record) != null || this.indexOf(record) >= 0
    );
  }

  indexOf(feature) {
    return this.records.findIndex(record => record.id === feature.id);
  }

  findRecordById(id) {
    const index = this.records.findIndex(record => record.id === id);
    if (index > -1) {
      return this.records[index];
    }
  }

  removeRecord(record) {
    const oldIndex = this.records.findIndex(rec => record.id === rec.id);
    if (oldIndex === -1) return;

    this.records.splice(oldIndex, 1);
  }

  addRecord(record) {
    this.addFeature(record);
  }

  addFeature(feature) {
    const currentIndex = this.records.findIndex(
      record => record.id === feature.id
    );
    if (currentIndex >= 0) return;
    this.records.push(feature);
  }

  async reloadRecords(buildRecordScope) {
    this.records = await BookmarksTeamBacklog.buildDefaultRecordScope(
      buildRecordScope
    ).all();
  }

  compareFeature(record, other) {
    return compareNullsLast(
      getBookmarkRecordPosition(record)?.position,
      getBookmarkRecordPosition(other)?.position
    );
  }

  sortFeatures() {
    this.records = this.records.sort(this.compareFeature);
  }
}

export default BookmarksTeamBacklog;
